import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, Collapse, Drawer, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import { formatOperationDateTime } from 'shared/lib/dateFormatter';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import usePackingListHistory from '../../../hooks/usePackingListHistory';
import { EVENT_STYLE, HISTORY_EVENT, actorName, describeEvent, formatChangeValue, groupChanges, hasComparison } from '../../../lib/packingListHistory';
import DocStatusChip from './DocStatusChip';

export const HISTORY_DRAWER_WIDTH = 560;
export const HISTORY_DRAWER_MAX_WIDTH = '95vw';
/** 문서가 지워져 이력을 읽을 수 없을 때 서버가 주는 code. 다시 시도해도 같으므로 Retry 대신 Drawer를 닫는다. */
const DOCUMENT_NOT_FOUND = 'DOCUMENT_NOT_FOUND';

const cellSx = { fontSize: 11, py: 0.5, px: 1, verticalAlign: 'top', wordBreak: 'break-word' };
const headSx = { ...cellSx, fontWeight: 600, color: '#01354d', bgcolor: '#f3f6f8' };

/** 수정 전·후 비교표. 문서 헤더·품목·배분 묶음마다 칸 / Before / After 한 줄씩. */
export function ChangeTable({ changes }) {
    const groups = groupChanges(changes);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {groups.map((group) => (
                <Box key={group.key} sx={{ border: '1px solid #e6ebee', borderRadius: '6px', overflow: 'hidden', bgcolor: 'white' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#003a52', px: 1, py: 0.5, borderBottom: '1px solid #e6ebee' }}>{group.title}</Typography>
                    <Table size="small" aria-label={`Changes: ${group.title}`}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...headSx, width: '30%' }}>Field</TableCell>
                                <TableCell sx={{ ...headSx, width: '35%' }}>Before</TableCell>
                                <TableCell sx={{ ...headSx, width: '35%' }}>After</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {group.rows.map((row) => (
                                <TableRow key={row.key}>
                                    <TableCell sx={cellSx}>
                                        {row.label}
                                        {row.kind && <Typography component="span" sx={{ ml: 0.5, fontSize: 10, color: row.kind === 'added' ? '#2e7d32' : '#d92936' }}>({row.kind})</Typography>}
                                    </TableCell>
                                    <TableCell sx={{ ...cellSx, color: row.before == null ? 'text.disabled' : '#8a1c1c', textDecoration: row.before != null && row.after != null ? 'line-through' : 'none' }}>{formatChangeValue(row.before)}</TableCell>
                                    <TableCell sx={{ ...cellSx, color: row.after == null ? 'text.disabled' : '#1b5e20', fontWeight: row.after != null ? 600 : 400 }}>{formatChangeValue(row.after)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            ))}
        </Box>
    );
}

/** 타임라인 한 줄 — 사건·시각·담당자·요약, 비교가 있으면 펼치는 버튼. */
function TimelineEvent({ event, last }) {
    const [open, setOpen] = useState(false);
    const style = EVENT_STYLE[event.event] ?? { label: event.event, color: '#6b7a82' };
    const comparable = hasComparison(event);
    return (
        <Box sx={{ display: 'flex', gap: 1.5, position: 'relative' }} data-testid={`history-event-${event.key}`}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 14, flexShrink: 0 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: style.color, mt: '5px', flexShrink: 0 }} />
                {!last && <Box sx={{ flex: 1, width: 2, bgcolor: '#e6ebee', mt: 0.5 }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: style.color }}>{style.label}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{formatOperationDateTime(event.at) || '—'}</Typography>
                </Box>
                <Typography sx={{ fontSize: 11, color: '#003a52' }}>{actorName(event.by)}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, wordBreak: 'break-word' }}>
                    {event.event === HISTORY_EVENT.UPLOADED && <InsertDriveFileOutlinedIcon sx={{ fontSize: 12, flexShrink: 0 }} />}
                    {describeEvent(event)}
                </Typography>
                {comparable && (
                    <>
                        <Button size="small" onClick={() => setOpen((o) => !o)} aria-expanded={open} endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />} sx={{ fontSize: 11, px: 0.75, py: 0, mt: 0.5, minHeight: 0 }}>
                            {open ? 'Hide changes' : 'Compare before / after'}
                        </Button>
                        <Collapse in={open} unmountOnExit>
                            <ChangeTable changes={event.changes} />
                        </Collapse>
                    </>
                )}
            </Box>
        </Box>
    );
}

/**
 * PL 문서 한 건의 History. 매니저가 누가 언제 올리고·확정하고·고치고·배분했는지 시간순으로 보고, 수정은 전후를 비교한다.
 * doc이 null이면 닫힌다. 열어 둔 사이 동료가 문서를 지우면 닫고 지워졌다고 알린다.
 */
export default function PackingListHistoryDrawer({ doc, scope, commands, onClose }) {
    const dispatch = useDispatch();
    const open = Boolean(doc);
    const { status, data, error, code, reload } = usePackingListHistory(commands, { docId: doc?.id ?? null, scope, enabled: open });
    const deleted = open && status === 'error' && code === DOCUMENT_NOT_FOUND;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!deleted) return;
        dispatch(setSnackbar({ open: true, severity: 'info', message: 'This packing list was deleted.' }));
        onCloseRef.current();
    }, [deleted, dispatch]);

    const events = data?.events ?? [];
    const title = doc?.invoice_no || data?.document?.invoice_no || 'Untitled';
    return (
        <Drawer
            open={open}
            onClose={onClose}
            anchor="right"
            ModalProps={{ slotProps: { backdrop: { 'data-testid': 'history-drawer-backdrop' } } }}
            PaperProps={{ sx: { width: HISTORY_DRAWER_WIDTH, maxWidth: HISTORY_DRAWER_MAX_WIDTH, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#f8f8f8' } }}
            data-testid="history-drawer"
            aria-labelledby="packing-list-history-title"
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'white', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography id="packing-list-history-title" sx={{ fontSize: 13, fontWeight: 600, color: '#01354d' }}>History</Typography>
                    <Typography title={title} sx={{ fontSize: 12, color: '#003a52', minWidth: 0 }} noWrap>{title}</Typography>
                    {data?.document?.status && <DocStatusChip status={data.document.status} />}
                </Box>
                <Tooltip title="Close">
                    <IconButton size="small" aria-label="close history drawer" onClick={onClose}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
                {deleted ? null : status === 'error' ? (
                    <Alert severity="error" sx={{ fontSize: 12, py: 0 }} action={<Button size="small" onClick={reload} sx={{ fontSize: 11 }}>Retry</Button>}>
                        {error}
                    </Alert>
                ) : status !== 'ready' && !data ? (
                    <CustomSkeleton2 />
                ) : events.length === 0 ? (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center', py: 3 }}>No history available.</Typography>
                ) : (
                    <Box role="list" aria-label="Packing list history">
                        {events.map((event, index) => (
                            <Box key={event.key ?? event.seq} role="listitem">
                                <TimelineEvent event={event} last={index === events.length - 1} />
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Drawer>
    );
}
